/**
 * <cutbank-drawer> — an accessible slide-in panel.
 *
 * The component is a thin wrapper around a native <dialog> opened with
 * `showModal()`. The platform therefore provides, for free and correctly:
 *
 *   - a focus trap for as long as the drawer is open
 *   - Escape to close
 *   - inertness of all background content (no mouse or keyboard interaction)
 *   - rendering in the top layer, above every stacking context
 *
 * Body scroll locking is handled in CSS via the `scroll-lock` attribute
 * (see `html:has(dialog[scroll-lock][open])` in critical.css), so scrolling is
 * always restored when the drawer closes, including if it is closed by the
 * browser rather than by this script.
 *
 * Usage:
 *   <button type="button" data-drawer-open="menu-drawer">Menu</button>
 *
 *   <cutbank-drawer id="menu-drawer">
 *     <dialog scroll-lock>
 *       <button type="button" data-drawer-close>Close</button>
 *       ...
 *     </dialog>
 *   </cutbank-drawer>
 *
 * Progressive enhancement: triggers are hidden until this script upgrades the
 * component, so a no-JS visitor is never shown a control that cannot work.
 */
class CutbankDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');

    if (!this.dialog) return;

    this.addEventListener('click', this.handleClick);
    this.dialog.addEventListener('close', this.handleClose);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.dialog?.removeEventListener('close', this.handleClose);
  }

  handleClick = (event) => {
    // An explicit close control was activated.
    if (event.target.closest('[data-drawer-close]')) {
      this.close();
      return;
    }

    // A click that lands on the dialog itself is a click on the backdrop,
    // because the panel inside fills the dialog box completely.
    if (event.target === this.dialog) {
      this.close();
    }
  };

  handleClose = () => {
    // Native <dialog> already restores focus to the previously focused element,
    // but we restore it explicitly so behaviour is identical across browsers
    // and when the drawer is opened programmatically.
    if (this.trigger?.isConnected) {
      this.trigger.focus();
    }

    this.trigger = null;
  };

  /**
   * @param {HTMLElement} [trigger] The control that opened the drawer, which
   *   receives focus again once the drawer closes.
   */
  open(trigger) {
    if (!this.dialog || this.dialog.open) return;

    this.trigger = trigger ?? null;
    this.dialog.showModal();
  }

  close() {
    if (!this.dialog?.open) return;

    this.dialog.close();
  }
}

if (!customElements.get('cutbank-drawer')) {
  customElements.define('cutbank-drawer', CutbankDrawer);
}

/**
 * A single delegated listener opens any drawer. Delegation means triggers
 * added later — for example when the theme editor re-renders a section —
 * keep working without rebinding.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-open]');

  if (!trigger) return;

  const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));

  if (!(drawer instanceof CutbankDrawer)) return;

  event.preventDefault();
  drawer.open(trigger);
});

/**
 * Shared cart helpers.
 *
 * Quick add, the product form, the cart drawer and the cart page all mutate the
 * same cart, so every request goes through `cartRequest` below rather than each
 * feature writing its own fetch. That is what keeps them from drifting into
 * four subtly different cart systems.
 *
 * `Shopify.routes.root` carries the locale prefix, so these keep working on a
 * localised storefront.
 */
const cartRoot = () => window.Shopify?.routes?.root || '/';

const announceToCart = (message) => {
  const region = document.getElementById('cart-live-region');

  if (region) region.textContent = message;
};

const refreshCartCount = async () => {
  try {
    const response = await fetch(cartRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return;

    const cart = await response.json();

    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = '(' + cart.item_count + ')';
    });
  } catch {
    /* The counter is cosmetic; never let it break an add. */
  }
};

/**
 * Every region the cart endpoints should re-render for us. The drawer is always
 * present; the cart page adds a second one when it is the current template.
 *
 * @returns {string[]} Section ids, de-duplicated.
 */
const cartSectionIds = () => {
  const ids = new Set();

  document.querySelectorAll('[data-cart-section]').forEach((node) => {
    if (node.dataset.cartSection) ids.add(node.dataset.cartSection);
  });

  return [...ids];
};

/**
 * Describes the currently focused control well enough to find it again after a
 * region has been replaced, so a keyboard user is not dropped to the top of the
 * drawer every time they press the quantity stepper.
 */
const cartFocusSelector = () => {
  const active = document.activeElement;
  const line = active?.closest?.('[data-line-key]');

  if (!line) return null;

  const key = `[data-line-key="${CSS.escape(line.dataset.lineKey)}"]`;

  if (active.matches('[data-line-step]')) {
    return `${key} [data-line-step="${CSS.escape(active.dataset.lineStep)}"]`;
  }

  if (active.matches('[data-line-input]')) return `${key} [data-line-input]`;
  if (active.matches('[data-line-remove]')) return `${key} [data-line-remove]`;

  return null;
};

/**
 * Swaps in section HTML returned by the Section Rendering API.
 *
 * Only the marked region inside each section is replaced. In the drawer that
 * region sits inside the <dialog>, so an open drawer stays open and keeps its
 * focus trap while its contents change underneath.
 */
const renderCartSections = (sections) => {
  if (!sections) return;

  const restoreTo = cartFocusSelector();
  const parser = new DOMParser();

  Object.entries(sections).forEach(([id, html]) => {
    const target = document.querySelector(`[data-cart-section="${CSS.escape(id)}"]`);

    if (!target || typeof html !== 'string') return;

    const source = parser
      .parseFromString(html, 'text/html')
      .querySelector(`[data-cart-section="${CSS.escape(id)}"]`);

    if (source) target.innerHTML = source.innerHTML;
  });

  if (!restoreTo) return;

  const replacement = document.querySelector(restoreTo);

  if (replacement) {
    replacement.focus();
    return;
  }

  // The line the focus was on is gone — most likely it was just removed. Move
  // focus to the surviving list, or to the region itself once it is empty.
  const fallback =
    document.querySelector('[data-cart-lines]') || document.querySelector('[data-cart-section]');

  if (fallback) {
    fallback.setAttribute('tabindex', '-1');
    fallback.focus();
  }
};

/**
 * One serial queue for every cart mutation.
 *
 * The cart is a single server-side resource, so overlapping writes race: two
 * quantity changes sent together can be applied in either order. Chaining them
 * means the second request is always built from, and applied after, the first.
 */
let cartQueue = Promise.resolve();

/**
 * @param {string} endpoint Cart endpoint, relative to the locale root — for
 *   example `cart/add.js`.
 * @param {FormData|object} payload Request body.
 * @param {object} [options]
 * @param {boolean} [options.render=true] Ask for, and swap in, the cart
 *   regions. Set false for changes that alter no displayed value.
 * @returns {Promise<object>} The parsed response.
 * @throws {Error} With Shopify's own description when the request is rejected.
 */
const cartRequest = (endpoint, payload, options = {}) => {
  const send = async () => {
    const sections = options.render === false ? [] : cartSectionIds();
    const sectionsUrl = window.location.pathname + window.location.search;
    let request;

    if (payload instanceof FormData) {
      if (sections.length > 0) {
        payload.set('sections', sections.join(','));
        payload.set('sections_url', sectionsUrl);
      }

      request = { method: 'POST', headers: { Accept: 'application/json' }, body: payload };
    } else {
      const body = { ...payload };

      if (sections.length > 0) {
        body.sections = sections;
        body.sections_url = sectionsUrl;
      }

      request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      };
    }

    const response = await fetch(cartRoot() + endpoint, request);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.description || result?.message || '');
    }

    renderCartSections(result.sections);

    return result;
  };

  const result = cartQueue.then(send, send);

  // Keep the chain alive after a rejection, but never leave it unhandled.
  cartQueue = result.then(
    () => {},
    () => {}
  );

  return result;
};

/**
 * Opens the cart drawer, if the theme has one on the page. Called after a
 * successful add only — a failed add leaves the visitor where they are, with
 * the error announced in place.
 */
const openCartDrawer = (trigger) => {
  const drawer = document.getElementById('cart-drawer');

  if (drawer instanceof CutbankDrawer) drawer.open(trigger);
};

/**
 * Quick add.
 *
 * Every add control is a real `POST` form to /cart/add, so with JavaScript
 * disabled the browser submits it and lands on the cart. Here we intercept
 * that submit and post to the AJAX cart instead, which keeps the visitor in
 * place.
 *
 * Feedback is announced through a single polite live region rather than by
 * moving focus, so a screen reader hears the result without losing its place.
 * The submitting form is marked busy for the duration, which is what stops a
 * second click from adding the same item twice.
 */
(function quickAdd() {
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-quick-add]');

    if (!form) return;

    event.preventDefault();

    // Already in flight — ignore the repeat rather than adding twice.
    if (form.hasAttribute('aria-busy')) return;

    const button = form.querySelector('[data-quick-add-button]');
    const label = form.querySelector('[data-quick-add-label]');
    const original = label?.textContent ?? '';

    form.setAttribute('aria-busy', 'true');
    button?.setAttribute('aria-disabled', 'true');

    if (label) label.textContent = form.dataset.labelLoading || 'Adding';

    try {
      const result = await cartRequest('cart/add.js', new FormData(form));

      if (label) label.textContent = form.dataset.labelAdded || 'Added';

      announceToCart(
        `${result.product_title || ''} ${form.dataset.messageSuccess || 'added to cart'}`.trim()
      );

      await refreshCartCount();

      openCartDrawer(button ?? undefined);

      window.setTimeout(() => {
        if (label) label.textContent = original;
      }, 2400);
    } catch (error) {
      if (label) label.textContent = original;
      announceToCart(error.message || form.dataset.messageError || 'Could not add to cart');
    } finally {
      form.removeAttribute('aria-busy');
      button?.removeAttribute('aria-disabled');
    }
  });
})();

/**
 * Pins the header once the page has scrolled, which shortens it from 96px to
 * 80px per the approved design.
 *
 * A passive listener with a requestAnimationFrame guard keeps this off the
 * scroll critical path. The class drives a CSS height change only — no layout
 * is measured here.
 */
(function pinHeaderOnScroll() {
  const header = document.querySelector('.section-header');

  if (!header) return;

  let ticking = false;

  const update = () => {
    header.classList.toggle('section-header--pinned', window.scrollY > 8);
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    },
    { passive: true }
  );

  update();
})();

/**
 * <product-gallery> — thumbnails drive the stage on desktop; the swipe rail
 * drives the progress dashes on mobile.
 *
 * No carousel library. The mobile behaviour is native scroll snapping, and
 * this only keeps the dashes and the pressed thumbnail in step with it.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-gallery-stage]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.items = [...this.querySelectorAll('[data-gallery-item]')];
    this.dashes = [...this.querySelectorAll('[data-gallery-dash]')];

    if (!this.stage) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.show(thumb.dataset.galleryThumb, true);
      });
    });

    // Keep the dashes honest while the rail is swiped.
    if (this.items.length > 1 && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.mark(entry.target.dataset.galleryItem);
          });
        },
        { root: this.stage, threshold: 0.6 }
      );

      this.items.forEach((item) => this.observer.observe(item));
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** Marks the active thumbnail and dash without moving the rail. */
  mark(mediaId) {
    if (!mediaId) return;

    // Only the thumbnail column reports a pressed state; the secondary grid
    // buttons are plain "show this view" controls.
    this.thumbs.forEach((thumb) => {
      if (thumb.hasAttribute('aria-pressed')) {
        thumb.setAttribute('aria-pressed', String(thumb.dataset.galleryThumb === mediaId));
      }
    });

    this.dashes.forEach((dash) => {
      dash.toggleAttribute('data-active', dash.dataset.galleryDash === mediaId);
    });
  }

  /**
   * @param {string} mediaId Media to display.
   * @param {boolean} [scroll] Also bring it into view on the mobile rail.
   */
  show(mediaId, scroll) {
    const target = this.items.find((item) => item.dataset.galleryItem === mediaId);

    if (!target) return;

    this.items.forEach((item) => {
      item.toggleAttribute('data-active', item === target);
    });

    this.mark(mediaId);

    if (scroll && this.stage.scrollWidth > this.stage.clientWidth) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.stage.scrollTo({ left: target.offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}

/**
 * <product-form> — variant selection, quantity and add to cart.
 *
 * Every lookup is scoped to `this`, so two forms on one page can never drive
 * each other. The payload parsed here is the small one the Liquid snippet
 * emits; the full product JSON is never sent to the browser.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.idInput = this.querySelector('[data-variant-id]');
    this.optionInputs = [...this.querySelectorAll('[data-option-value]')];
    this.message = this.querySelector('[data-variant-message]');

    const payload = this.querySelector('[data-variant-data]');

    try {
      this.variants = payload ? JSON.parse(payload.textContent) : [];
    } catch {
      this.variants = [];
    }

    if (!this.form) return;

    this.form.addEventListener('submit', this.handleSubmit);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('click', this.handleQuantity);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
  }

  /** Includes buttons that sit outside the element but target this form. */
  get addButtons() {
    const external = this.form?.id
      ? [...document.querySelectorAll('[data-add-button][form="' + this.form.id + '"]')]
      : [];

    return [...this.querySelectorAll('[data-add-button]'), ...external];
  }

  get selectedOptions() {
    const values = [];

    this.optionInputs.forEach((input) => {
      if (input.checked) values[Number(input.dataset.optionPosition) - 1] = input.value;
    });

    return values;
  }

  handleQuantity = (event) => {
    const stepper = event.target.closest('[data-quantity-step]');

    if (!stepper) return;

    const input = this.querySelector('[data-quantity-input]');

    if (!input) return;

    const previous = Number(input.value);
    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const next = Number(input.value) + Number(stepper.dataset.quantityStep) * step;

    input.value = String(Math.min(max, Math.max(min, next)));
  };

  handleChange = (event) => {
    if (!event.target.matches('[data-option-value]')) return;

    this.updateVariant();
  };

  updateVariant() {
    const selected = this.selectedOptions;
    const match = this.variants.find(
      (variant) =>
        variant.options.length === selected.length &&
        variant.options.every((option, index) => option === selected[index])
    );

    // Reflect the chosen value beside each option's label.
    this.querySelectorAll('[data-option-index]').forEach((group) => {
      const input = group.querySelector('[data-option-value]:checked');
      const readout = group.querySelector('[data-option-selected]');

      if (input && readout) readout.textContent = input.value;
    });

    if (this.message) this.message.hidden = Boolean(match);

    if (!match) {
      // No variant answers this combination — refuse to let it be submitted.
      if (this.idInput) this.idInput.value = '';
      this.setAddState({ disabled: true, label: 'unavailable' });
      return;
    }

    if (this.idInput) this.idInput.value = String(match.id);

    this.setAddState({
      disabled: !match.available,
      label: match.available ? 'default' : 'soldOut',
    });

    this.updatePrice(match);
    this.updateInventory(match);
    this.updateMedia(match);
    this.updateUrl(match);
  }

  setAddState(state) {
    this.addButtons.forEach((button) => {
      button.disabled = state.disabled;

      const target = button.querySelector('[data-add-label]');

      if (!target) return;

      const map = {
        default: button.dataset.labelDefault,
        soldOut: button.dataset.labelSoldOut,
        unavailable: button.dataset.labelUnavailable,
      };

      target.textContent = map[state.label] || button.dataset.labelDefault;
    });
  }

  updatePrice(variant) {
    const root = this.closest('[data-product-root]') || document;

    root.querySelectorAll('[data-price-current]').forEach((node) => {
      node.textContent = variant.price;
    });

    root.querySelectorAll('[data-price-compare]').forEach((node) => {
      const value = node.querySelector('[data-price-compare-value]');

      if (variant.compare_at) {
        if (value) value.textContent = variant.compare_at;
        node.hidden = false;
      } else {
        node.hidden = true;
      }
    });
  }

  /**
   * Rebuilds the availability line. The low-stock branch repeats exactly the
   * conditions the Liquid snippet applies, so the message can never claim
   * more scarcity than the inventory data supports.
   */
  updateInventory(variant) {
    const region = document.querySelector('[data-inventory-region]');
    const status = region?.querySelector('[data-inventory-status]');

    if (!status) return;

    const inventory = variant.inventory || {};
    const threshold = Number(this.dataset.lowStockThreshold || 0);
    const isLow =
      variant.available &&
      Boolean(inventory.managed) &&
      inventory.policy === 'deny' &&
      inventory.quantity > 0 &&
      inventory.quantity <= threshold;

    let state = variant.available ? 'in-stock' : 'sold-out';
    if (isLow) state = 'low';

    status.className = 'inventory inventory--' + state;

    let text = variant.available ? this.dataset.inStock : this.dataset.soldOut;

    if (isLow) {
      text = (this.dataset.lowStock || '').replace('[count]', String(inventory.quantity));
    }

    status.textContent = '';

    if (state !== 'sold-out') {
      const dot = document.createElement('span');

      dot.className = state === 'low' ? 'inventory__dot' : 'inventory__dot inventory__dot--ok';
      dot.setAttribute('aria-hidden', 'true');
      status.appendChild(dot);
    }

    status.appendChild(document.createTextNode(text || ''));
  }

  updateMedia(variant) {
    if (!variant.media_id) return;

    const gallery = document.querySelector('[data-product-root] product-gallery');

    if (gallery && typeof gallery.show === 'function') {
      gallery.show(String(variant.media_id), true);
    }
  }

  /**
   * Rewrites only the `variant` parameter, leaving every other parameter and
   * the locale-prefixed path exactly as they were.
   */
  updateUrl(variant) {
    if (!window.history || !window.history.replaceState) return;

    const url = new URL(window.location.href);

    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url);
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    if (this.form.hasAttribute('aria-busy')) return;

    const buttons = this.addButtons;
    const labels = buttons.map((button) => button.querySelector('[data-add-label]'));
    const originals = labels.map((label) => (label ? label.textContent : ''));

    this.form.setAttribute('aria-busy', 'true');
    buttons.forEach((button, index) => {
      button.setAttribute('aria-disabled', 'true');
      if (labels[index]) labels[index].textContent = button.dataset.labelLoading || 'Adding';
    });

    try {
      const result = await cartRequest('cart/add.js', new FormData(this.form));

      buttons.forEach((button, index) => {
        if (labels[index]) labels[index].textContent = button.dataset.labelAdded || 'Added';
      });

      const title = result.product_title || '';

      announceToCart((title + ' ' + (this.dataset.messageSuccess || 'added to cart')).trim());

      await refreshCartCount();

      openCartDrawer(buttons[0]);

      window.setTimeout(() => {
        labels.forEach((label, index) => {
          if (label) label.textContent = originals[index];
        });
      }, 2400);
    } catch (error) {
      labels.forEach((label, index) => {
        if (label) label.textContent = originals[index];
      });

      announceToCart(error.message || this.dataset.messageError || 'Could not add to cart');
    } finally {
      this.form.removeAttribute('aria-busy');
      buttons.forEach((button) => button.removeAttribute('aria-disabled'));
    }
  };
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

/**
 * <product-recommendations> — asks Shopify's recommendations endpoint for a
 * rendered section and swaps it in. Returning nothing is a valid answer: the
 * element simply stays empty and the page has no recommendations band.
 */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;

    if (!url || this.dataset.loaded) return;

    this.dataset.loaded = 'true';

    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject()))
      .then((text) => {
        const fresh = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('product-recommendations');

        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {
        /* No recommendations is an outcome, not an error worth surfacing. */
      });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

/**
 * Collection filtering and sorting.
 *
 * Both are ordinary GET forms that already work unaided. This submits them on
 * change so a shopper is not left hunting for an Apply button; it is a pure
 * enhancement and nothing here is required for either to function.
 */
document.addEventListener('change', (event) => {
  const sortForm = event.target.closest('[data-sort-form]');

  if (sortForm && event.target.matches('[data-sort-select]')) {
    sortForm.submit();
    return;
  }

  const filterForm = event.target.closest('[data-filter-form]');

  // The mobile sheet has its own Apply and Clear controls, so only the
  // desktop rail applies as soon as a box is ticked.
  if (filterForm && filterForm.classList.contains('filters--rail')) {
    filterForm.submit();
  }
});

/**
 * Mobile filter sheet — staged selections.
 *
 * Choices made inside the sheet are held until "Show products" is pressed, so
 * the page never reloads mid-decision and closing the sheet cannot silently
 * change the URL. Applying is an ordinary GET submit of the same form, which
 * keeps it locale-aware and working without JavaScript.
 *
 * The apply button starts with the count of the filters already applied. That
 * figure stops being true the moment something is staged, and the prospective
 * count cannot be known without asking the server again — so at that point the
 * label falls back to the generic wording rather than guessing a number.
 */
(function stagedFilterSheet() {
  /*
   * Compares the live controls against the markup's own defaults, which are
   * what the URL rendered. Reading the defaults rather than snapshotting on
   * first interaction means the baseline can never be captured after the
   * shopper has already changed something.
   */
  const readState = (form, useDefaults) =>
    [...form.elements]
      .filter((el) => el.name && el.type !== 'submit' && el.type !== 'reset')
      .map((el) =>
        el.type === 'checkbox'
          ? `${el.name}=${useDefaults ? el.defaultChecked : el.checked}`
          : `${el.name}=${useDefaults ? el.defaultValue : el.value}`
      )
      .join('&');

  /** Swaps the apply label as soon as the staged set stops matching the URL. */
  const sync = (form) => {
    const apply = form.querySelector('[data-filter-apply]');

    if (!apply) return;

    const untouched = readState(form, false) === readState(form, true);

    apply.textContent = untouched ? apply.dataset.labelApplied : apply.dataset.labelGeneric;
  };

  const drawerForm = (node) => {
    const form = node?.closest?.('[data-filter-form].filters--drawer');

    return form || null;
  };

  document.addEventListener('change', (event) => {
    const form = drawerForm(event.target);

    if (form) sync(form);
  });

  // Clearing empties the staged selections; it does not navigate.
  document.addEventListener('click', (event) => {
    const clear = event.target.closest('[data-filter-clear]');
    const form = drawerForm(clear);

    if (!clear || !form) return;

    event.preventDefault();

    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = false;
    });

    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.value = '';
    });

    sync(form);
  });

  /*
   * Closing without applying puts the sheet back to what the URL actually
   * says, so reopening always reflects the filters in force.
   */
  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;

      event.target.querySelectorAll('[data-filter-form].filters--drawer').forEach((form) => {
        form.reset();
        sync(form);
      });
    },
    true
  );
})();

/**
 * Cart line controls, for both the drawer and the cart page.
 *
 * These are delegated from the document rather than bound per line, which means
 * they keep working on the markup the Section Rendering API swaps in after every
 * change — and it means the drawer and the cart page run the same code instead
 * of two implementations that drift apart.
 *
 * Without JavaScript the same controls remain a real Shopify cart form: the
 * quantity inputs are `updates[key]` fields and the page has an Update button,
 * so a scriptless visitor can still change quantities and check out.
 */
(function cartLineControls() {
  const showError = (line, message) => {
    const target = line.querySelector('[data-line-error]');

    if (target) target.textContent = message;
  };

  /**
   * Applies a new quantity to one line.
   *
   * @param {HTMLElement} line The `[data-cart-line]` element.
   * @param {number} quantity The absolute quantity; 0 removes the line.
   * @param {number} previous The quantity to put back if the change is
   *   rejected — the field has already been moved optimistically.
   */
  const applyQuantity = async (line, quantity, previous) => {
    line.setAttribute('aria-busy', 'true');
    showError(line, '');
    announceToCart(line.dataset.messageLoading || '');

    try {
      await cartRequest('cart/change.js', { id: line.dataset.lineKey, quantity });
      await refreshCartCount();

      announceToCart(
        quantity === 0 ? line.dataset.messageRemoved || '' : line.dataset.messageUpdated || ''
      );
    } catch (error) {
      const message = error.message || line.dataset.messageError || '';
      const input = line.querySelector('[data-line-input]');

      // Nothing was re-rendered, so put the field back to what the cart still
      // holds rather than leaving the rejected number on screen.
      if (input && previous !== undefined) input.value = String(previous);

      showError(line, message);
      announceToCart(message);
    } finally {
      line.removeAttribute('aria-busy');
    }
  };

  /**
   * Rapid presses of a stepper are collapsed into one request carrying the
   * final quantity. The field itself has already moved, so the visitor sees
   * every press; only the network traffic is coalesced. Sending each press
   * separately would be correct too — requests are serialised below — but the
   * number would visibly bounce as each intermediate answer came back.
   */
  const pending = new Map();

  const scheduleQuantity = (line, quantity, previous, delay = 300) => {
    const key = line.dataset.lineKey;

    window.clearTimeout(pending.get(key));

    pending.set(
      key,
      window.setTimeout(() => {
        pending.delete(key);

        // The region may have been re-rendered while we waited, so address the
        // line by key rather than holding on to a detached element.
        const current = document.querySelector(`[data-line-key="${CSS.escape(key)}"]`) || line;

        applyQuantity(current, quantity, previous);
      }, delay)
    );
  };

  document.addEventListener('click', (event) => {
    const stepper = event.target.closest('[data-line-step]');
    const remove = event.target.closest('[data-line-remove]');
    const line = (stepper || remove)?.closest('[data-cart-line]');

    if (!line) return;

    event.preventDefault();

    if (remove) {
      scheduleQuantity(line, 0, undefined, 0);
      return;
    }

    const input = line.querySelector('[data-line-input]');

    if (!input) return;

    const previous = Number(input.value);
    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = input.max === '' ? Infinity : Number(input.max);
    const next = previous + step * Number(stepper.dataset.lineStep);

    if (next < min || next > max) return;

    // Update the field first so a second click reads the new value, and so the
    // number visibly responds before the request comes back.
    input.value = String(next);

    scheduleQuantity(line, next, previous);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line-input]');
    const line = input?.closest('[data-cart-line]');

    if (!line) return;

    const quantity = Number(input.value);

    if (!Number.isFinite(quantity) || quantity < 0) return;

    scheduleQuantity(line, quantity, Number(input.defaultValue), 0);
  });

  /**
   * The cart page's note. Saved on blur so nothing is sent per keystroke, and
   * without re-rendering, since the note changes no totals.
   */
  document.addEventListener('change', async (event) => {
    const note = event.target.closest('[data-cart-note]');

    if (!note) return;

    try {
      await cartRequest('cart/update.js', { note: note.value }, { render: false });
    } catch {
      /* The note is saved again on checkout submit, so a failure is not fatal. */
    }
  });
})();

/**
 * <predictive-search> — suggestions under the search field.
 *
 * The results are rendered by Shopify from the `predictive-search` section, so
 * the shopper's query is escaped by Liquid on the server. Nothing here builds
 * HTML from what was typed; the only value read back into the page is a
 * translated count.
 *
 * Progressive enhancement: the field stays an ordinary GET form to
 * `/search`, and everything below only ever adds suggestions above it.
 */
class PredictiveSearch extends HTMLElement {
  static MIN_LENGTH = 2;
  static DEBOUNCE = 250;

  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.panel = this.querySelector('[data-predictive-panel]');
    this.target = this.querySelector('[data-predictive-target]');
    this.status = this.querySelector('[data-predictive-status]');
    this.clear = this.querySelector('[data-predictive-clear]');

    if (!this.input || !this.target) return;

    this.timer = null;
    this.controller = null;

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.target.id || 'predictive-search-results');
    this.target.id = this.input.getAttribute('aria-controls');

    this.input.addEventListener('input', this.handleInput);
    this.addEventListener('keydown', this.handleKeydown);
    this.clear?.addEventListener('click', this.handleClear);

    // A drawer that closes should not reopen showing the previous search.
    this.closest('dialog')?.addEventListener('close', this.reset);

    this.toggleClear();
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.controller?.abort();
  }

  toggleClear() {
    if (this.clear) this.clear.hidden = this.input.value === '';
  }

  handleClear = () => {
    this.reset();
    this.input.value = '';
    this.toggleClear();
    this.input.focus();
  };

  handleInput = () => {
    this.toggleClear();
    window.clearTimeout(this.timer);

    const query = this.input.value.trim();

    if (query.length < PredictiveSearch.MIN_LENGTH) {
      this.reset();
      return;
    }

    this.timer = window.setTimeout(() => this.search(query), PredictiveSearch.DEBOUNCE);
  };

  /**
   * Escape closes the suggestions before the drawer, and the arrow keys walk
   * the results, so the whole overlay is usable without a pointer.
   */
  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.target.childElementCount > 0) {
      // `stopPropagation` alone only stops other listeners; the browser would
      // still treat Escape as a close request and shut the surrounding
      // <dialog>, dismissing the whole overlay in one press. Preventing the
      // default keeps this first Escape for the suggestions, so a second one
      // closes the drawer.
      event.preventDefault();
      event.stopPropagation();
      this.reset();
      this.input.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const results = [...this.querySelectorAll('[data-search-result]')];

    if (results.length === 0) return;

    event.preventDefault();

    const current = results.indexOf(document.activeElement);
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    const next = current === -1 ? 0 : current + offset;

    if (next < 0) {
      this.input.focus();
      return;
    }

    results[Math.min(next, results.length - 1)].focus();
  };

  reset = () => {
    window.clearTimeout(this.timer);
    this.controller?.abort();
    this.controller = null;

    this.target.innerHTML = '';
    this.panel?.removeAttribute('aria-busy');
    this.input.setAttribute('aria-expanded', 'false');

    if (this.status) this.status.textContent = '';
  };

  async search(query) {
    // Abandon whatever is in flight: only the newest query can still be right.
    this.controller?.abort();
    this.controller = new AbortController();

    const url = new URL(this.dataset.url || cartRoot() + 'search/suggest', window.location.origin);

    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');

    if (this.dataset.resources) url.searchParams.set('resources[type]', this.dataset.resources);
    if (this.dataset.limit) url.searchParams.set('resources[limit]', this.dataset.limit);

    this.panel?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, { signal: this.controller.signal });

      if (!response.ok) throw new Error();

      const html = await response.text();
      const results = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-predictive-results]');

      // Assigning HTML lets the live document parse it, which is what starts the
      // thumbnail loads. Adopting nodes out of the parsed document leaves
      // lazily-loaded images that never fetch.
      this.target.innerHTML = results ? results.outerHTML : '';

      this.input.setAttribute('aria-expanded', this.target.childElementCount > 0 ? 'true' : 'false');

      // The section phrases and pluralises the count; we only relay it.
      if (this.status) {
        this.status.textContent = this.querySelector('[data-result-summary]')?.textContent ?? '';
      }
    } catch (error) {
      // An aborted request is a newer one taking over, not a failure.
      if (error.name === 'AbortError') return;

      this.target.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');

      if (this.status) this.status.textContent = this.dataset.messageError || '';
    } finally {
      this.panel?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
